import React, { useMemo, useState, useEffect } from "react";
import { Card, CardBody, Col, Button, UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem, } from "reactstrap";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import KpiTile from "./KpiTile";
import { SeriesChart } from "./graphs/Graphs";
import { mergeTimeSeries, toMWh } from "./graphs/utils.js";
import { faEuroSign, faTree, faUsers, faChartArea, faHome, faSun, faMagic, faCaretDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAppQuery } from "../hooks/useAppQuery";
import { fetchKpisBySiteIdAtTime } from "../api/graphs_api/kpisApi";
import { fetchSimulatedMetricsBySiteId } from "../api/graphs_api/simulatedMetricsApi";
import Loading from "./Loading";
import { ChevronLeft, ChevronRight } from "lucide-react";

const pct = v => Number.isFinite(v) ? Math.round(v * 100) : 0;
const kg2 = v => Number.isFinite(v) ? Number((v / 1000).toFixed(2)) : 0;
const id = v => Number.isFinite(v) ? v : 0;

const KPI_MODES = [
  { id: "opti", labelKey: "SteeringPref.Optischedule", icon: faMagic },
  { id: "co2",  labelKey: "SteeringPref.CO2", icon: faTree },
];

const TILE_SPEC = [
  { id: 1, type: "cost_optimization", unit: "%", labelKey: "performance.overview.tiles.cost_savings.label", valueFn: pct, selectedIcon: faEuroSign },
  { id: 2, type: "carbon_saving", unit: "kgCO₂", labelKey: "performance.overview.tiles.carbon_saving.label", valueFn: kg2, selectedIcon: faTree },
  { id: 3, type: "consume_right_time_score", unit: "/5", labelKey: "performance.overview.tiles.consume_right_time.label", valueFn: id, selectedIcon: faUsers },
  { id: 4, type: "peak_reduction", unit: "%", labelKey: "performance.overview.tiles.peak_reduction.label", valueFn: pct, selectedIcon: faChartArea },
  { id: 5, type: "autonomy", unit: "%", labelKey: "performance.overview.tiles.autonomy.label", valueFn: pct, selectedIcon: faHome },
  { id: 6, type: "inject_right_time_score", unit: "/5", labelKey: "performance.overview.tiles.inject_right_time.label", valueFn: id, selectedIcon: faSun },
];

function useTiles(site, schedule_type) {
  return useMemo(() => {
    const byType = Object.fromEntries((site?.kpis ?? []).map(k => [k.kpi_type, k.kpi_value]));
  
    return TILE_SPEC.map(s => ({ id: s.id, unit: s.unit, labelKey: s.labelKey, value: s.valueFn(byType[`${s.type}_${schedule_type}`]), selectedIcon: s.selectedIcon }));
  }, [site, schedule_type]);
}

function useMergedRows(site, timezone, schedule_type, intervalMinutes = 15) {
  return useMemo(() => {
    return mergeTimeSeries({
      userTimezone: timezone,
      intervalMinutes,
      sources: [
        {
          rows: site?.prices,
          apply: (p, out) => { out.price = p.price; },
        },
        {
          rows: site?.carbon_intensity,
          apply: (c, out) => {
            out.co2_intensity_g_per_kwh = Number((c.carbon_intensity_value * 1000).toFixed(2));
          },
        },
        {
          rows: site?.simulatedMetrics,
          apply: (m, out) => {
            if (m.metric_type === `grid_with_${schedule_type}`) out.with_us_wh = m.metric_value;
            if (m.metric_type === "grid_with_basic") out.without_us_wh = m.metric_value;
          },
        },
      ],
    });
  }, [site, timezone, schedule_type, intervalMinutes]);
}

const ENERGY_UNIT = "Wh";
const AXIS_LABEL = {
  consumptionInjectionWh: "performance.overview.axes.consumption_injection_wh",
  priceEurPerMWh: "performance.overview.axes.price_eur_per_mwh",
  co2Intensity: "performance.overview.axes.co2_intensity_g_per_kwh",
};

const SERIES_WITH_US = { label: "performance.overview.series.grid_with.label", key: "with_us_wh", type: "line", fill: true, pointRadius: 0, colorKey: "measured", axis: "y", order: 1, unit: ENERGY_UNIT};
const SERIES_WITHOUT_US = { label: "performance.overview.series.grid_without.label", key: "without_us_wh", type: "line", fill: true, pointRadius: 0, colorKey: "forecasted", axis: "y", order: 1, unit: ENERGY_UNIT };
const SERIES_PRICE = { label: "performance.overview.series.price.label", key: "price", type: "line", colorKey: "price", axis: "y1", order: 0, unit: "€/MWh", pointRadius: 1, mapFn: e => toMWh(e.price) };
const SERIES_CO2 = { label: "performance.overview.series.co2.label", key: "co2_intensity_g_per_kwh", type: "line", colorKey: "co2", axis: "y1", order: 0, unit: "gCO₂eq/kWh", pointRadius: 1 };

const BASE_SPEC = { unit: ENERGY_UNIT, yLeft: AXIS_LABEL.consumptionInjectionWh };
const bars = [SERIES_WITH_US, SERIES_WITHOUT_US];
const s = (...extra) => bars.concat(extra);

const kpiSpecs = {
  1: {
    ...BASE_SPEC,
    title: "performance.overview.kpi.cost_savings.title",
    intro: "performance.overview.kpi.cost_savings.intro",
    yRight: AXIS_LABEL.priceEurPerMWh,
    foot: ["performance.overview.kpi.cost_savings.foot"],
    series: s(SERIES_PRICE)
  },
  2: {
    ...BASE_SPEC,
    title: "performance.overview.kpi.carbon_saving.title",
    intro: "performance.overview.kpi.carbon_saving.intro",
    yRight: AXIS_LABEL.co2Intensity,
    foot: ["performance.overview.kpi.carbon_saving.foot",
      "performance.overview.kpi.carbon_saving.link.suffix"],
    series: s(SERIES_CO2)
  },
  3: {
    ...BASE_SPEC,
    title: "performance.overview.kpi.consume_right_time.title",
    intro: "performance.overview.kpi.consume_right_time.intro",
    yRight: AXIS_LABEL.priceEurPerMWh,
    foot: ["performance.overview.kpi.consume_right_time.foot"],
    series: s(SERIES_PRICE)
  },
  4: {
    ...BASE_SPEC,
    title: "performance.overview.kpi.peak_reduction.title",
    intro: "performance.overview.kpi.peak_reduction.intro.pos",
    series: s()
  },
  6: {
    ...BASE_SPEC,
    title: "performance.overview.kpi.inject_right_time.title",
    intro: "performance.overview.kpi.inject_right_time.intro",
    yRight: AXIS_LABEL.priceEurPerMWh,
    series: s(SERIES_PRICE)
  },
};

export default function PerformanceOverviewBlock({ site, userTimezone, showKpiModeSelector = false }) {
  const { t } = useTranslation();
  const [active, setActive] = useState(null);
  const [kpiMode, setKpiMode] = useState("opti");
  useEffect(() => {
    if (!showKpiModeSelector) {
      setKpiMode("opti");
    }
  }, [showKpiModeSelector]);

  const timezone = userTimezone || localStorage.getItem("user-timezone") || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timeout = parseInt(window.appConfig?.universalTimeout);

  const [selectedDate, setSelectedDate] = useState(() =>
    DateTime.now().setZone(timezone).minus({ days: 1 }).startOf("day")
  );

  // Keep selectedDate in sync if timezone changes
  useEffect(() => {
    setSelectedDate(prev => {
      if (!prev) return DateTime.now().setZone(timezone).minus({ days: 1 }).startOf("day");
      return prev.setZone(timezone, { keepLocalTime: true });
    });
  }, [timezone]);

  const allowedDates = useMemo(() => {
    const list = [];
    for (let i = 1; i <= 7; i++) {
      list.push(DateTime.now().setZone(timezone).minus({ days: i }).startOf("day"));
    }
    return list;
  }, [timezone]);

  const earliestAllowed = allowedDates[allowedDates.length - 1];
  const latestAllowed = allowedDates[0];

  const selectedLocalStart = useMemo(() => selectedDate.startOf("day"), [selectedDate]);
  const selectedLocalEnd = useMemo(() => selectedLocalStart.plus({ days: 1 }).minus({ minutes: 1 }), [selectedLocalStart]);

  const apiFromDate = useMemo(() => selectedLocalStart.toUTC().toISO(), [selectedLocalStart]);
  const apiToDate = useMemo(() => selectedLocalEnd.toUTC().toISO(), [selectedLocalEnd]);

  const formatUiDate = dt => dt.toFormat("dd/LL/yy");

  const goPreviousDay = () => {
    if (selectedLocalStart <= earliestAllowed) return;
    setSelectedDate(prev => prev.minus({ days: 1 }));
  };
  const goNextDay = () => {
    if (selectedLocalStart >= latestAllowed) return;
    setSelectedDate(prev => prev.plus({ days: 1 }));
  };

  const [showDatePicker, setShowDatePicker] = useState(false);
  const toggleDatePicker = () => setShowDatePicker(v => !v);
  const selectDateFromPicker = (dt) => {
    setSelectedDate(dt);
    setShowDatePicker(false);
  };

  const simMetricTypes = ["grid_with_opti", "grid_with_co2", "grid_with_basic"];

  const {
    data: fetchedKpis,
    isLoading: kpisLoading,
    error: kpisError,
  } = useAppQuery(
    ["kpis-by-site", site?.id, apiFromDate, timeout],
    fetchKpisBySiteIdAtTime,
    { enabled: !!site?.id && !!apiFromDate }
  );

  const {
    data: fetchedSimMetrics,
    isLoading: simLoading,
    error: simError,
  } = useAppQuery(
    ["sim-metrics-by-site", site?.id, apiFromDate, apiToDate, simMetricTypes, timeout],
    fetchSimulatedMetricsBySiteId,
    { enabled: !!site?.id && !!apiFromDate && !!apiToDate && !!simMetricTypes }
  );

  const kpisFailed = Boolean(kpisError);
  const simFailed = Boolean(simError);

  const depsByTileId = {
    1: { kpis: true, prices: true, sim: true },
    2: { kpis: true, carbon: true, sim: true },
    3: { kpis: true, prices: true, sim: true },
    4: { kpis: true, sim: true },
    5: { kpis: true },
    6: { kpis: true, prices: true, sim: true },
  };

  const tileIsLoading = id => {
    const d = depsByTileId[id] || { kpis: true };
    return (d.kpis && kpisLoading) || (d.sim && simLoading);
  };

  const tileHasError = id => {
    const d = depsByTileId[id] || { kpis: true };
    return Boolean((d.kpis && kpisFailed) || (d.sim && simFailed));
  };

  const fetchedSite = useMemo(() => ({
    ...site,
    kpis: fetchedKpis ?? [],
    simulatedMetrics: fetchedSimMetrics ?? [],
    carbon_intensity: site.carbon_intensity ?? [],
    prices: site.prices ?? [],
  }), [site, fetchedKpis, fetchedSimMetrics]);

  const tilesFromFetched = useTiles(fetchedSite, kpiMode);
  const mergedRows = useMergedRows(fetchedSite, userTimezone, kpiMode);
  const dataByKpi = useMemo(() => ({ 1: mergedRows, 2: mergedRows, 3: mergedRows, 4: mergedRows, 6: mergedRows }), [mergedRows]);

  const openTile = id => {
    if (tileIsLoading(id) || tileHasError(id)) return;
    setActive(prev => (prev === id ? null : id));
  };

  const renderActivePanel = () => {
    if (!active) return null;

    if (active === 5) {
      const tile = tilesFromFetched.find(x => x.id === 5);
      return (
        <div className="p-3">
          <div className="fw-semibold mb-2" style={{ color: "var(--brand-green)", fontSize: "2.25rem", lineHeight: 1 }}>
            {tile.value} {tile.unit}
          </div>
          <p className="mb-0" style={{ whiteSpace: "pre-line" }}>{t("performance.overview.kpi.autonomy.body")}</p>
        </div>
      );
    }

    const spec = kpiSpecs[active];
    if (!spec) return null;

    const rows = dataByKpi[active];
    const tile = tilesFromFetched.find(x => x.id === active);

    let introKeys = Array.isArray(spec.intro) ? spec.intro : [spec.intro];
    if (active === 4) introKeys = [(tile?.value ?? 0) < 0 ? "performance.overview.kpi.peak_reduction.intro.neg" : "performance.overview.kpi.peak_reduction.intro.pos"];

    return (
      <>
        <div className="fw-semibold mb-2" style={{ color: "var(--brand-green)", fontSize: "2.25rem", lineHeight: 1 }}>
          {tile?.value}{tile?.unit}
        </div>

        {introKeys.filter(Boolean).map(k => (
          <p className="mb-3" key={k} style={{ whiteSpace: "pre-line" }}>{t(k)}</p>
        ))}

        <SeriesChart
          rows={rows}
          chartKind="bar"
          chartTitle={spec.title}
          yAxisName={spec.yLeft}
          yAxisRightName={spec.yRight}
          unit={spec.unit}
          userTimezone={userTimezone}
          graphHeight={45}
          series={spec.series}
          intervalMinutes={15}
        />

        <div className="mt-3">
          {spec.foot?.map(k => {
            if (k === "performance.overview.kpi.carbon_saving.link.suffix") {
              return (
                <p key={k}>
                  <a href={t("performance.overview.kpi.carbon_saving.link.url")} target="_blank" rel="noreferrer" style={{ color: "var(--brand-sky)" }}>
                    {t("performance.overview.kpi.carbon_saving.link.text")}
                  </a>{" "}
                  {t("performance.overview.kpi.carbon_saving.link.suffix")}
                </p>
              );
            }
            return <p key={k} style={{ whiteSpace: "pre-line" }}>{t(k)}</p>;
          })}
        </div>
      </>
    );
  };

  return (
    <Col className="px-0">
      <Card className="mb-3 border-0 shadow-lg">
        <CardBody className="p-2 cardbody-md-default">
          <div className="performance-overview-block-header pl-4 pr-4 mb-4" style={{ position: "relative" }}>
            <div>
              <h5 className="text-center text-muted fw-semibold">
                {t("performance.overview.header.title")}
              </h5>
            </div>
            <div className="performance-overview-block-header-picker">
              <Button color="link" className={`status-chevron mb-1 ${selectedLocalStart <= earliestAllowed ? "text-muted" : ""}`} data-testid="chevronLeft" onClick={goPreviousDay} aria-disabled={selectedLocalStart <= earliestAllowed}>
                <ChevronLeft strokeWidth={3} size={36} />
              </Button>

              <button
                type="button"
                onClick={toggleDatePicker}
                aria-haspopup="true"
                aria-expanded={showDatePicker}
                className="btn btn-link text-decoration-none text-center text-muted fw-semibold"
                style={{ cursor: "pointer" }}
                data-testid="selectedDateButton"
              >
                {selectedLocalStart >= latestAllowed ?
                  <h5 className="text-center text-muted fw-semibold">
                    {t("performance.overview.header.yesterday")}
                  </h5> :
                  <h5 className="text-center text-muted fw-semibold">
                    {formatUiDate(selectedLocalStart)}
                  </h5>
                }
              </button>

              <Button color="link" className={`status-chevron mb-1 ${selectedLocalStart >= latestAllowed ? "text-muted" : ""}`} data-testid="chevronRight" onClick={goNextDay} aria-disabled={selectedLocalStart >= latestAllowed}>
                <ChevronRight strokeWidth={3} size={36} />
              </Button>

              {showDatePicker && (
                <div style={{ position: "absolute", left: "50%", transform: "translateX(-50%)", top: "3.5rem", zIndex: 999 }}>
                  <div className="card shadow-sm" style={{ minWidth: 180 }}>
                    <div className="list-group list-group-flush">
                      {allowedDates.map(d => (
                        <button
                          type="button"
                          key={d.toISO()}
                          className={`list-group-item list-group-item-action ${d.hasSame(selectedLocalStart, "day") ? "active" : ""}`}
                          onClick={() => selectDateFromPicker(d)}
                        >
                          {d.toFormat("cccc dd/LL/yy")}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* KPI mode selector (optional) */}
          {showKpiModeSelector && (
            <div className="footer-pickers-row justify-content-center mb-3">
              <div className="footer-picker d-flex align-items-center">
                <span className="text-muted">
                  {t("performance.overview.mode.label")}
                </span>

                <UncontrolledDropdown>
                  <DropdownToggle
                    className="dropdown d-flex align-items-center dropdown-toggle-btn"
                  >
                    <FontAwesomeIcon
                      icon={KPI_MODES.find(m => m.id === kpiMode)?.icon}
                      className="mr-2"
                    />
                    {t(KPI_MODES.find(m => m.id === kpiMode)?.labelKey)}
                    <FontAwesomeIcon
                      icon={faCaretDown}
                      style={{ marginLeft: "0.5rem", fontSize: "1.0em" }}
                      aria-label="Mode dropdown chevron"
                    />
                  </DropdownToggle>

                  <DropdownMenu
                    end
                    className="dropdown-menu"
                  >
                    {KPI_MODES.map(mode => (
                      <DropdownItem
                        key={mode.id}
                        active={mode.id === kpiMode}
                        onClick={() => setKpiMode(mode.id)}
                      >
                        <FontAwesomeIcon icon={mode.icon} className="mr-2" />
                        {t(mode.labelKey)}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </UncontrolledDropdown>
              </div>
            </div>
          )}
          <div className="tile-grid mb-4">
            {tilesFromFetched.map(tile => {
              const loading = tileIsLoading(tile.id);
              const error = tileHasError(tile.id);

              if (loading) {
                return (
                  <div key={tile.id} className="tile-card shadow-sm border border-lt mb-3" style={{ borderRadius: "0.5rem", padding: 0 }}>
                    <div style={{ minHeight: 120, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Loading fullscreen={false} />
                    </div>
                  </div>
                );
              }

              if (error) {
                return (
                  <div key={tile.id} className="tile-card shadow-sm border border-lt mb-3" style={{ borderRadius: "0.5rem", padding: 0 }}>
                    <div style={{ minHeight: 120, display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center" }}>
                      <div className="text-muted fw-semibold">{t("N/A")}</div>
                    </div>
                  </div>
                );
              }

              return (
                <KpiTile
                  key={tile.id}
                  value={tile.value}
                  unit={tile.unit}
                  label={t(tile.labelKey)}
                  active={active === tile.id}
                  onClick={() => openTile(tile.id)}
                  selectedIcon={tile.selectedIcon}
                />
              );
            })}
          </div>

          {active && (
            renderActivePanel()
          )}
        </CardBody>
      </Card>
    </Col>
  );
}
